import { ChangeEvent, SyntheticEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BootstrapForm from 'react-bootstrap/Form';

import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Link from 'components/common/Link/Link';
import TimeOptions from 'components/common/TimeOptions/TimeOptions';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import { parseDuration, parseFutureDateTime, timezoneMoment } from 'common/util/time';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import './SilenceAlertsButton.scss';
import 'components/common/Dropdown/Dropdown.scss';

const TIME_OPTIONS = ['1 hour', '24 hours', '1 week', '2 hours', '2 days', '2 weeks', '4 hours', '3 days', '4 weeks'];

/**
 * The time menu dropdown button to silence alerts.
 * When an time option is selected or the 'mute alert' form is submitted.
 * It invokes the submitForm callback with the time option or states.
 *
 * @param {function} submitForm       The callback function on form submit.
 *
 */

export type SilenceAlertInput = {
  silenceComment?: string;
  silenceUntil?: string;
  selectedOption?: string;
};

type SilenceAlertsButtonProps = {
  submitForm: (input: SilenceAlertInput) => Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'lg' | undefined;
  testId?: ELEMENT;
};

function SilenceAlertsButton({
  submitForm,
  disabled = false,
  size = undefined,
  testId,
}: SilenceAlertsButtonProps): JSX.Element {
  const [silenceInputs, setSilenceInputs] = useState({ silenceUntil: '', silenceComment: '' });
  const [showOverlay, setShowOverlay] = useState(false);
  const timezone = useRecoilValue(timezoneState);

  /**
   * Handles input onChange and set states.
   */
  const handleInputOnChange = (ev: ChangeEvent<HTMLInputElement>): void => {
    setSilenceInputs({ ...silenceInputs, [ev.target.name]: ev.target.value });
  };

  /**
   * Submit the form with states.
   */
  const submitFormWithStates = (ev: SyntheticEvent): void => {
    ev.preventDefault();
    const parsed = parseFutureDateTime(silenceInputs.silenceUntil, timezone);
    silenceInputs.silenceUntil = String(parsed?.milliseconds);
    submitForm(silenceInputs).then(() => {
      setShowOverlay(false);
    });
  };

  /**
   * Reset the state every time the triggering button is clicked.
   */
  const handleTrigger = (): void => {
    setSilenceInputs({ silenceUntil: '', silenceComment: '' });
  };

  const formatTimeOption = (time: string): string => timezoneMoment(timezone)().add(parseDuration(time)).toISOString();

  const handleTimeClick = (selectedTime: string): void => {
    const selectedOption = formatTimeOption(selectedTime);
    submitForm({ selectedOption }).then(() => {
      setShowOverlay(false);
    });
  };

  // the upper part of the dropdown
  const timeOptionsSection = (
    <TimeOptions header="Mute this alert for:" options={TIME_OPTIONS} clickHandler={handleTimeClick} testId={testId} />
  );

  // the lower part of the dropdown
  const formSection = (
    <BootstrapForm onSubmit={submitFormWithStates} className="SilenceAlertsButton__SilenceUntilForm">
      <BootstrapForm.Group controlId="SilenceAlertsButton__SilenceUntilFormGroup">
        <BootstrapForm.Control
          required
          as="input"
          type="text"
          name="silenceUntil"
          autoFocus
          value={silenceInputs.silenceUntil}
          onChange={handleInputOnChange}
        />
        <BootstrapForm.Text className="SilenceAlertsButton__SilenceUntilText">
          3 hours, Jan 1 2025, etc. &nbsp;
          <Link href={HELP_ROUTE.HELP_TIME_REFERENCE} className="link" openInNewTab>
            See more
          </Link>
        </BootstrapForm.Text>
        <BootstrapForm.Control
          as="textarea"
          rows={3}
          name="silenceComment"
          placeholder="Optional comment"
          value={silenceInputs.silenceComment}
          onChange={handleInputOnChange}
        />
      </BootstrapForm.Group>
      <Button type="submit" variant={ButtonVariant.PRIMARY}>
        Mute
      </Button>
    </BootstrapForm>
  );

  const DropdownButtonBody = (
    <div className="SilenceAlertsButton__MenuDropdown Dropdown__Menu" data-test-id={ELEMENT.alertMuteDropdown}>
      {timeOptionsSection}
      <div className="SilenceAlertsButton__FormSection">
        <div className="SilenceAlertsButton__FormLabel">Or for/until:</div>
        {formSection}
      </div>
    </div>
  );

  return (
    <div className="SilenceAlertsButton">
      <OverlayTrigger
        placement="bottom-start"
        overlay={DropdownButtonBody}
        showOverlay={showOverlay}
        setShowOverlay={setShowOverlay}
      >
        <Button
          size={size}
          disabled={disabled}
          onClick={handleTrigger}
          leftIcon={<VolumeOffIcon fontSize="small" />}
          rightIcon={<ArrowDropDownIcon fontSize="small" />}
          testId={testId}
        >
          Mute
        </Button>
      </OverlayTrigger>
    </div>
  );
}

export default SilenceAlertsButton;
